import styles from './styles.module.css';
import { Button, Grid } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { ShopContext } from 'Context/ShopContext';
import { useHistory, useParams } from 'react-router-dom';
import ShopSettingsService from 'services/ShopSettingsService';
import { AlertMessagesContext } from 'react-alert-messages';
import { camelToTitleCase } from 'helpers/StringHelper';
import { ROUTES } from 'const';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import Info from 'components/utils/Alert/Info';
import DataTable from 'components/utils/DataTable';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ExternalSyncFailedSaleDialog from 'components/popups/ExternalSyncFailedSaleDialog';
import { useTranslation } from 'react-i18next';

const ZOHO_CLIENT_ID = '1000.5G3NCITWHTK5VCUX90PYHA8FYMJVGY';

export default function ZohoSettings() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const history = useHistory();
  const { subTab } = useParams();
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [isLoading, setLoading] = useState(false);
  const [shopSettings, setShopSettings] = useState(false);
  const [unsyncSales, setUnsyncedSales] = useState([]);
  const [selectedFailedSale, setSelectedFailedSale] = useState(null);

  const isFeatureExpired = useMemo(() => featureIsExpired(shop?.zoho_support_validity), [shop?.zoho_support_validity]);

  const redirectUrl = `https://${window.location.host}/console/settings/zohobook/callback`;

  const onClickConnectZoho = () => {
    window.location.href =
      'https://accounts.zoho.in/oauth/v2/auth' +
      '?scope=ZohoBooks.fullaccess.all' +
      `&client_id=${ZOHO_CLIENT_ID}` +
      '&response_type=code' +
      `&redirect_uri=${redirectUrl}` +
      '&access_type=offline' +
      `&state=${shop.id}`;
  };

  const onClickDisconnectZoho = useCallback(async () => {
    setLoading(true);
    try {
      await ShopSettingsService.detachZohoAccount();
      setShopSettings(null);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  }, [postAlertMessage]);

  const onErrorLogSelected = (log) => {
    setSelectedFailedSale(log);
  };

  const onCloseSelectedSaleError = () => {
    setSelectedFailedSale(null);
  };

  const getSyncFailedSales = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await ShopSettingsService.getExternalSyncFailedSales();
      setUnsyncedSales(resp);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  }, [postAlertMessage]);

  useEffect(() => {
    if (isFeatureExpired || shopSettings?.external_sales_sync_platform !== 'Zoho Books') {
      return;
    }
    getSyncFailedSales();
  }, [postAlertMessage, isFeatureExpired, shopSettings?.external_sales_sync_platform, getSyncFailedSales]);

  useEffect(() => {
    if (shopSettings) {
      return;
    }
    const loadSettings = async () => {
      setLoading(true);
      try {
        const settings = await ShopSettingsService.getShopSettings();
        setShopSettings(settings);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoading(false);
    };
    loadSettings();
  }, [postAlertMessage, shopSettings]);

  useEffect(() => {
    if (!subTab) return;

    if (subTab === 'disconnect') {
      onClickDisconnectZoho();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) return;

    const processResponse = async () => {
      setLoading(true);
      try {
        await ShopSettingsService.attachZohoAccount({
          authorization_code: code,
          shop_id: state,
          redirect_uri: redirectUrl,
        });
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      history.push(ROUTES.SETTINGS_TAB.replace(':tab', 'zohobook'));
      setLoading(false);
    };

    processResponse();
  }, [subTab, redirectUrl, postAlertMessage, history, onClickDisconnectZoho]);

  const zohobookSettings = useMemo(() => {
    if (
      !shopSettings ||
      shopSettings.external_sales_sync_platform !== 'Zoho Books' ||
      !shopSettings.external_sales_sync_config
    ) {
      return;
    }

    const {
      refresh_token: refreshToken,
      code,
      expense_account_id: defaultExpenseAccountId,
      income_account_id: defaultIncomeAccountId,
      asset_account_id: defaultAssetAccountId,
      default_customer_id: defaultCustomerId,
    } = shopSettings.external_sales_sync_config;

    return {
      refreshToken,
      code,
      defaultExpenseAccountId,
      defaultIncomeAccountId,
      defaultAssetAccountId,
      defaultCustomerId,
    };
  }, [shopSettings]);

  const onClickAddQuickbookItems = async () => {
    setLoading(true);
    try {
      const resp = await ShopSettingsService.fetchZohobookProducts();
      postAlertMessage({
        text: resp?.message,
        type: 'success',
      });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  };

  const onClickSyncAllFailedSales = async () => {
    setLoading(true);
    try {
      await ShopSettingsService.syncAllExternalSales();
      await getSyncFailedSales();
      postAlertMessage({ text: tManagement('Sales synced successfully'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  };

  const onClickSyncFailedSales = async (saleId) => {
    setLoading(true);
    try {
      await ShopSettingsService.syncExternalSale(saleId);
      await getSyncFailedSales();
      postAlertMessage({ text: tManagement('Sales synced successfully'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoading(false);
  };

  const headerData = [
    {
      label: tCommon('Sale'),
      id: 'sale_id',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'callback',
      viewRender: (data) => DateTimeHelpers.getDate(data.logs[0]?.created_at),
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (data) => DateTimeHelpers.getTime(data.logs[0]?.created_at),
    },
    {
      label: tCommon('Attempts'),
      id: 'attempts',
      type: 'callback',
      viewRender: (data) => String(data.logs.length),
    },
    {
      label: tCommon('Message'),
      id: 'message',
      type: 'callback',
      viewRender: (data) => data.logs[0]?.message ?? '-',
    },
  ];

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <Box container spacing={2}>
          {!shopSettings || (isLoading && !shopSettings) ? (
            <div style={{ padding: 20 }}>
              <Skeleton variant="rectangular" height={200} animation="wave" />
              <Skeleton variant="text" style={{ marginTop: 20 }} animation="wave" />
              <Skeleton variant="text" animation="wave" />
            </div>
          ) : isFeatureExpired ? (
            <RestrictionInfo
              title={tManagement('Zohobook support feature not available')}
              content={tManagement('To get zohobooks support options, contact our support team.')}
            />
          ) : (
            <>
              <Box className={styles.subHeader}>
                <span>{tManagement('Zohobook Config')}</span>
              </Box>
              <Box p={2}>
                {zohobookSettings ? (
                  <Box>
                    <Box mb={3}>
                      <Typography color="success" variant="h5">
                        {tCommon('Connected')}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {Object.keys(zohobookSettings).map((key) => (
                        <Grid key={key} container item spacing={1}>
                          <Grid item xs={6}>
                            <Typography sx={{ overflowWrap: 'break-word' }}>{camelToTitleCase(key)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ overflowWrap: 'break-word' }} color="primary">
                              <strong>{zohobookSettings[key]}</strong>
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  <Typography color="error" variant="h5">
                    {tCommon('Not connected')}
                  </Typography>
                )}
              </Box>
              <Box p={2}>
                {zohobookSettings ? (
                  <Box display="flex">
                    <Button variant="contained" color="error" className={styles.button} onClick={onClickDisconnectZoho}>
                      {tManagement('Disconnect Zoho')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!unsyncSales || unsyncSales.length === 0}
                      className={styles.button}
                      onClick={onClickSyncAllFailedSales}
                    >
                      {tCommon('Sync Sales')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      className={styles.button}
                      onClick={onClickAddQuickbookItems}
                    >
                      {tCommon('Fetch Products')}
                    </Button>
                  </Box>
                ) : (
                  <Button variant="contained" color="primary" className={styles.button} onClick={onClickConnectZoho}>
                    {tManagement('Connect Zoho')}
                  </Button>
                )}
              </Box>
              {zohobookSettings && (
                <>
                  <hr className={styles.divider} />
                  <Box className={styles.subHeader}>
                    <span>{tCommon('Sales Sync Errors')}</span>
                  </Box>
                  {unsyncSales && unsyncSales.length ? (
                    <DataTable columns={headerData} rows={unsyncSales} rowClickListener={onErrorLogSelected} />
                  ) : (
                    !isLoading && (
                      <Info
                        severity="success"
                        title={tManagement('All your sales are synced to Zohobook')}
                        content={tManagement('You have no failed sale to sync to quickbook.')}
                      />
                    )
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </div>

      <ExternalSyncFailedSaleDialog
        open={Boolean(selectedFailedSale)}
        log={selectedFailedSale}
        onClose={onCloseSelectedSaleError}
        onRetry={onClickSyncFailedSales}
      />
    </div>
  );
}
